/**
 * Parent dashboard shell: side nav + main column.
 *
 * Desktop (md+): a sticky left sidebar with grouped nav + profile chip.
 * Mobile (<md): a sticky top bar with hamburger → slide-in drawer with the
 * same grouped nav + profile chip.
 */
import { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EmergencyModeBanner } from './EmergencyMode';
import { PanicFAB } from './PanicFAB';
import { AskKio } from './AskKio';
import { KidFocusProvider } from './KidFocusContext';
import { ProfileChip } from './ProfileChip';
import { SafeIdAlertBanner } from './SafeIdAlertBanner';
import { PetGuardAlertBanner } from './PetGuardAlertBanner';
import {
  Activity,
  Home,
  MapPin,
  Users,
  ShieldCheck,
  Clock,
  PhoneCall,
  Siren,
  HeartHandshake,
  MessageCircle,
  Coins,
  Car,
  CalendarDays,
  QrCode,
  PawPrint,
  ShieldAlert,
  Menu,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';

type Tint = 'blue' | 'coral' | 'green' | 'amber' | 'slate';

interface NavItem {
  to: string;
  labelKey: string;
  icon: typeof Home;
  end?: boolean;
  tint: Tint;
}

interface NavGroup {
  labelKey: string;
  items: NavItem[];
}

const TINT_CLASS: Record<Tint, string> = {
  blue: 'text-[#4f6bff]',
  coral: 'text-[#ff7a59]',
  green: 'text-[#16a34a]',
  amber: 'text-[#d39400]',
  slate: 'text-[color:var(--kz-color-ink-muted)]',
};

const GROUPS: NavGroup[] = [
  {
    labelKey: 'nav.group_overview',
    items: [
      { to: '/', labelKey: 'nav.home', icon: Home, end: true, tint: 'blue' },
      { to: '/map', labelKey: 'nav.map', icon: MapPin, tint: 'blue' },
      { to: '/timeline', labelKey: 'nav.timeline', icon: Activity, tint: 'blue' },
      { to: '/calendar', labelKey: 'nav.calendar', icon: CalendarDays, tint: 'blue' },
    ],
  },
  {
    labelKey: 'nav.group_family',
    items: [
      { to: '/children', labelKey: 'nav.children', icon: Users, tint: 'coral' },
      { to: '/circle', labelKey: 'nav.trusted_circle', icon: HeartHandshake, tint: 'coral' },
      { to: '/chat', labelKey: 'nav.family_chat', icon: MessageCircle, tint: 'coral' },
    ],
  },
  {
    labelKey: 'nav.group_safety',
    items: [
      { to: '/safety', labelKey: 'nav.safety', icon: ShieldCheck, tint: 'green' },
      { to: '/scam-shield', labelKey: 'nav.scam_shield', icon: ShieldAlert, tint: 'green' },
      { to: '/screen-time', labelKey: 'nav.screen_time', icon: Clock, tint: 'green' },
      { to: '/emergency', labelKey: 'nav.emergency', icon: PhoneCall, tint: 'green' },
      { to: '/sos', labelKey: 'nav.sos_history', icon: Siren, tint: 'green' },
    ],
  },
  {
    labelKey: 'nav.group_lifestyle',
    items: [
      { to: '/rewards', labelKey: 'nav.rewards', icon: Coins, tint: 'amber' },
      { to: '/driving', labelKey: 'nav.driving', icon: Car, tint: 'amber' },
      { to: '/safe-id', labelKey: 'nav.safe_id', icon: QrCode, tint: 'amber' },
      { to: '/pets', labelKey: 'nav.pet_guard', icon: PawPrint, tint: 'amber' },
    ],
  },
  // Account-level pages (Settings, Billing) live in the ProfileChip menu,
  // not in the main app nav — see [[ProfileChip]].
];

function NavItemLink({ item, onNavigate }: { item: NavItem; onNavigate?: () => void }) {
  const { t } = useTranslation();
  const { to, labelKey, icon: Icon, end, tint } = item;
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'group relative flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors',
          isActive
            ? 'bg-[color:var(--kz-color-brand-primary)]/12 font-semibold text-[color:var(--kz-color-brand-primary)] shadow-[inset_2px_0_0_var(--kz-color-brand-primary)]'
            : 'text-[color:var(--kz-color-ink-secondary)] hover:bg-[color:var(--kz-color-surface-sunken)] hover:text-[color:var(--kz-color-ink-primary)]',
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon className={cn('size-4', !isActive && TINT_CLASS[tint])} />
          {t(labelKey)}
        </>
      )}
    </NavLink>
  );
}

function NavList({ onNavigate }: { onNavigate?: () => void }) {
  const { t } = useTranslation();
  return (
    <nav className="flex flex-col gap-5">
      {GROUPS.map((group) => (
        <div key={group.labelKey}>
          <p className="mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-wider text-[color:var(--kz-color-ink-muted)]">
            {t(group.labelKey)}
          </p>
          <div className="flex flex-col gap-0.5">
            {group.items.map((item) => (
              <NavItemLink key={item.to} item={item} onNavigate={onNavigate} />
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}

export function DashboardLayout() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <KidFocusProvider>
    <div className="min-h-screen bg-[color:var(--kz-color-surface-sunken)]">
      {/* Mobile top bar */}
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-[color:var(--kz-color-border-subtle)] bg-[color:var(--kz-color-surface-card)] px-4 md:hidden">
        <div className="flex items-center gap-2">
          <img src="/logo-mark.svg" alt="Kidzio" width={28} height={28} className="size-7" />
          <span className="text-sm font-semibold">Kidzio</span>
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open menu"
          className="grid size-9 place-items-center rounded-md border border-[color:var(--kz-color-border-subtle)] text-[color:var(--kz-color-ink-primary)]"
        >
          <Menu className="size-5" />
        </button>
      </header>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-40 md:hidden" role="dialog" aria-modal="true">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <aside className="absolute left-0 top-0 flex h-full w-72 max-w-[85vw] flex-col bg-[color:var(--kz-color-surface-card)] px-4 py-5 shadow-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <img src="/logo-mark.svg" alt="Kidzio" width={28} height={28} className="size-7" />
                <span className="text-sm font-semibold">Kidzio</span>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="grid size-9 place-items-center rounded-md text-[color:var(--kz-color-ink-secondary)] hover:bg-[color:var(--kz-color-surface-sunken)]"
              >
                <X className="size-5" />
              </button>
            </div>
            <div className="mt-6 flex-1 overflow-y-auto">
              <NavList onNavigate={() => setOpen(false)} />
            </div>
            <div className="mt-4">
              <ProfileChip />
            </div>
          </aside>
        </div>
      )}

      <div className="mx-auto flex max-w-7xl">
        <aside className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col border-r border-[color:var(--kz-color-border-subtle)] bg-[color:var(--kz-color-surface-card)] px-4 py-6 md:flex">
          <div className="mb-6 flex items-center gap-2 px-2">
            <img src="/logo-mark.svg" alt="Kidzio" width={32} height={32} className="size-8" />
            <span className="text-sm font-semibold tracking-tight">Kidzio</span>
          </div>
          <div className="flex-1 overflow-y-auto">
            <NavList />
          </div>
          <div className="mt-4">
            <ProfileChip />
          </div>
        </aside>

        <main className="kz-dashboard-canvas relative min-h-screen flex-1 bg-[color:var(--kz-color-surface-sunken)]">
          <EmergencyModeBanner />
          <div className="px-4 pt-4 sm:px-6 md:px-10">
            <SafeIdAlertBanner />
            <PetGuardAlertBanner />
          </div>
          <div key={location.pathname} className="kz-fade-up">
            <Outlet />
          </div>
          <PanicFAB />
          <AskKio />
        </main>
      </div>
    </div>
    </KidFocusProvider>
  );
}
